/**
 * Notifications tab — granular email opt-outs. Tier-locked rows
 * (Account Health Check digest) show a Steel upsell when the user
 * doesn't have access.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Row = ({ id, title, description, checked, onChange, locked, lockedLabel }) => (
  <div className="flex items-start gap-4 py-4 border-b border-[#E2E8F0] last:border-0" data-testid={`notif-row-${id}`}>
    <div className="flex-1">
      <Label htmlFor={id} className="text-sm font-bold text-[#020617]">{title}</Label>
      <p className="text-xs text-[#64748B] mt-0.5 leading-relaxed">{description}</p>
      {locked && (
        <Link to="/pricing" className="inline-flex items-center gap-1 text-xs text-[#0052FF] font-semibold mt-1.5 hover:underline" data-testid={`notif-upsell-${id}`}>
          <Lock className="w-3 h-3" />
          {lockedLabel || 'Upgrade to Steel to unlock'}
        </Link>
      )}
    </div>
    <Switch
      id={id}
      checked={checked && !locked}
      disabled={locked}
      onCheckedChange={onChange}
      data-testid={`notif-switch-${id}`}
    />
  </div>
);

const NotificationsTab = () => {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/account/notifications`, { withCredentials: true })
      .then((r) => setPrefs(r.data))
      .catch(() => toast.error('Could not load notification preferences.'));
  }, []);

  const flip = async (key, next) => {
    if (!prefs) return;
    setSaving(true);
    const previous = prefs[key];
    setPrefs({ ...prefs, [key]: next });
    try {
      const r = await axios.put(`${API}/account/notifications`, { [key]: next }, { withCredentials: true });
      setPrefs(r.data);
    } catch (err) {
      setPrefs({ ...prefs, [key]: previous });
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail.message : detail;
      toast.error(msg || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) return <div className="text-sm text-[#64748B]">Loading…</div>;

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 max-w-2xl" data-testid="notifications-tab">
      <h2 className="text-xl font-bold text-[#020617] mb-1">Notifications</h2>
      <p className="text-sm text-[#64748B] mb-4">Choose which emails Forge Ledger sends you. Transactional emails (password reset, account-deletion confirmation) are always sent.</p>

      <Row
        id="payment_receipts"
        title="Payment receipts"
        description="Stripe charge succeeded, failed, or refunded. Strongly recommended — these are the audit trail for your subscription."
        checked={prefs.payment_receipts}
        onChange={(v) => flip('payment_receipts', v)}
      />
      <Row
        id="product_updates"
        title="Product updates"
        description="Big shipped features, breaking changes, and launch announcements. ~1 email a month."
        checked={prefs.product_updates}
        onChange={(v) => flip('product_updates', v)}
      />
      <Row
        id="marketing"
        title="Marketing & offers"
        description="Founding-member promos, seasonal discounts, partnership perks. Off by default — opt in when you want them."
        checked={prefs.marketing}
        onChange={(v) => flip('marketing', v)}
      />
      <Row
        id="digest_enabled"
        title="Account Health Check (weekly digest)"
        description="Monday-morning summary of where your balances will be this week and any incoming shortfalls. Steel+ feature."
        checked={prefs.digest_enabled}
        onChange={(v) => flip('digest_enabled', v)}
        locked={!prefs.unlocked_digest}
        lockedLabel="Upgrade to Steel to unlock weekly digests"
      />
      {saving && <p className="text-xs text-[#94A3B8] mt-3">Saving…</p>}
    </div>
  );
};

export default NotificationsTab;
